/**
 * <ActivationAllocationSection> — « Heures par matière » (organism).
 *
 * La section optionnelle du composeur : elle n'apparaît que lorsque la répartition a un sens —
 * plusieurs matières, et strictement moins de matières que d'heures (sinon c'est une heure par
 * matière, et il n'y a rien à répartir). Cette règle appartient au view-model : ici, pas de lignes,
 * pas de section.
 *
 * **Repliée par défaut.** L'ouverture est de l'état d'INTERFACE : elle vit dans le composant, pas
 * dans les props. La répartition, elle, est de l'état MÉTIER : `rows` + `onChange`.
 *
 * Chaque ligne borne son propre pas : on ne descend jamais sous 1 h, et on ne monte pas au-delà de
 * `maxHours`, que le view-model calcule (les heures restantes une fois une heure réservée à chacune
 * des autres matières).
 */
import React, { useState } from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { Card } from "../../molecules/Card"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { List } from "../../molecules/List"
import { Row } from "../../atoms/containers/Row"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Text } from "../../atoms/typography/Text"
import { type UIActivationAllocationRow } from "../../../ports/primary/ui/types"

/** Le pas des deux boutons — le dessin du legacy, en 24 px. */
const STEP_SIZE = 24

export type ActivationAllocationSectionProps = {
    /** Les matières et leur volume. Vide → la section ne rend rien. */
    rows?: UIActivationAllocationRow[]
    onChange: (subjectId: string | number, hours: number) => void
    /**
     * Ouverte au premier rendu. C'est une VALEUR DE DÉPART, pas un pilotage : l'ouverture reste de
     * l'état d'interface et vit ici. Elle existe pour qu'une story — et une recette — puissent
     * regarder la répartition sans avoir à cliquer. Défaut `false`, comme le legacy.
     */
    defaultExpanded?: boolean
    testID?: string
}

export function ActivationAllocationSection(props: ActivationAllocationSectionProps) {
    const { rows = [], onChange, defaultExpanded = false, testID } = props
    const [expanded, setExpanded] = useState(defaultExpanded)

    if (rows.length === 0) return null

    if (!expanded) {
        return (
            <Button
                testID={testID}
                label="Répartir les heures entre les matières (optionnel)"
                variant="ghost"
                size="sm"
                iconName="chevron-right"
                iconPosition="right"
                textColor="text-muted"
                onPress={() => setExpanded(true)}
            />
        )
    }

    return (
        <List title="Heures par matière" titleIcon="balance-scale" testID={testID}>
            <View style={styles.rows}>
                {rows.map((row) => (
                    <AllocationRow key={String(row.subject.id)} row={row} onChange={onChange} />
                ))}
            </View>
        </List>
    )
}

/** Une matière, son volume, et les deux pas qui le changent. */
function AllocationRow(props: {
    row: UIActivationAllocationRow
    onChange: ActivationAllocationSectionProps["onChange"]
}) {
    const { row, onChange } = props
    const { subject, hours, maxHours } = row

    return (
        <Card p="2">
            <Row gap="2">
                <SubjectBadge subject={subject} />
                <Text size="text-md" weight="medium" style={styles.name}>
                    {subject.name}
                </Text>
                <IconButton
                    iconName="minus"
                    size={STEP_SIZE}
                    iconSize={14}
                    color="primary-soft"
                    iconColor={hours <= 1 ? "text-muted" : "primary"}
                    disabled={hours <= 1}
                    onPress={() => onChange(subject.id, Math.max(1, hours - 1))}
                    accessibilityLabel={`Retirer une heure à ${subject.name}`}
                />
                <Badge text={`${hours} h`} variant="primary" tinted />
                <IconButton
                    iconName="plus"
                    size={STEP_SIZE}
                    iconSize={14}
                    color="primary-soft"
                    iconColor={hours >= maxHours ? "text-muted" : "primary"}
                    disabled={hours >= maxHours}
                    onPress={() => onChange(subject.id, Math.min(maxHours, hours + 1))}
                    accessibilityLabel={`Ajouter une heure à ${subject.name}`}
                />
            </Row>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    rows: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
    },
    /** Le nom se replie ; les deux pas et le compte gardent leur place. */
    name: { flexShrink: 1 },
}))
